import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { areaUnitOptions } from '../../../../shared/contracts/job-request.js';

/** T077 header — the shape GET /v1/job-requests/:id returns. */
interface JobHeader {
  readonly jobRequestId: string;
  readonly jobNumber: string | null;
  readonly currentStatus: string;
  readonly customerName: string | null;
  readonly workTypeId: string | null;
  readonly areaRequested: string | number | null;
  readonly areaUnit: string | null;
  readonly latitude: string | number | null;
  readonly longitude: string | number | null;
  readonly remarks: string | null;
}

type Mode = 'FULL' | 'REMARKS' | 'LOCKED';

// M06-01 §20 — the edit window: everything while DRAFT, remarks only once
// SUBMITTED, nothing after the request has left sales.
function modeFor(status: string): Mode {
  if (status === 'DRAFT') return 'FULL';
  if (status === 'SUBMITTED') return 'REMARKS';
  return 'LOCKED';
}

const text = (v: unknown) => (v == null ? '' : String(v));

export function SalesJobEditPage(): ReactNode {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobHeader | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [workTypeId, setWorkTypeId] = useState('');
  const [areaRequested, setAreaRequested] = useState('');
  const [areaUnit, setAreaUnit] = useState('SQ_FT');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest<JobHeader>(`/v1/job-requests/${id}`, { token })
      .then((r) => {
        if (!alive) return;
        setJob(r);
        setCustomerName(text(r.customerName));
        setWorkTypeId(text(r.workTypeId));
        setAreaRequested(r.areaRequested == null ? '' : String(Number(r.areaRequested)));
        setAreaUnit(text(r.areaUnit) || 'SQ_FT');
        setLatitude(r.latitude == null ? '' : String(Number(r.latitude)));
        setLongitude(r.longitude == null ? '' : String(Number(r.longitude)));
        setRemarks(text(r.remarks));
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  const mode: Mode = useMemo(() => (job ? modeFor(job.currentStatus) : 'LOCKED'), [job]);

  function validate(): string | null {
    if (mode !== 'FULL') return null;
    if (!customerName.trim()) return t('sales.edit.err.customerName');
    if (!workTypeId.trim()) return t('sales.edit.err.workType');
    if (!(Number(areaRequested) > 0)) return t('sales.edit.err.area');
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (latitude === '' || Number.isNaN(lat) || lat < -90 || lat > 90) return t('sales.edit.err.latitude');
    if (longitude === '' || Number.isNaN(lng) || lng < -180 || lng > 180) return t('sales.edit.err.longitude');
    return null;
  }

  async function save() {
    if (!job || mode === 'LOCKED') return;
    const problem = validate();
    if (problem) { setError(problem); return; }
    setBusy(true); setError(null);
    try {
      const body = mode === 'FULL'
        ? {
            remarks: remarks.trim() || null,
            customerName: customerName.trim(),
            workTypeId: workTypeId.trim(),
            areaRequested: Number(areaRequested),
            areaUnit,
            latitude: Number(latitude),
            longitude: Number(longitude),
          }
        : { remarks: remarks.trim() || null };
      await apiRequest(`/v1/job-requests/${job.jobRequestId}`, { method: 'PUT', token, body });
      navigate(`/sales/jobs/${job.jobRequestId}`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  if (error && !job) return <main><p role="alert">{error}</p></main>;
  if (!job) return <main><p>{t('sales.edit.loading')}</p></main>;

  const headerDisabled = mode !== 'FULL';
  const modeLabel = mode === 'FULL' ? t('sales.edit.modeFull') : mode === 'REMARKS' ? t('sales.edit.modeLimited') : t('sales.edit.modeLocked');

  return (
    <main aria-label="sales-job-edit">
      <h1>{t('sales.edit.title', { code: job.jobNumber ?? t('sales.jobs.draft') })}</h1>
      <p aria-label="edit-mode">{modeLabel}</p>
      {mode === 'LOCKED' ? <p aria-label="locked-notice">{t('sales.edit.lockedNotice', { status: job.currentStatus })}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      <label>{t('sales.edit.customerName')}<input aria-label="customer-name" value={customerName} disabled={headerDisabled} onChange={(e) => setCustomerName(e.target.value)} /></label>
      <RecordPicker<{ workTypeId: string; workTypeCode?: string; workTypeName?: string }>
        endpoint="/master/work-types"
        idField="workTypeId"
        label={t('sales.edit.workType')}
        testId="work-type"
        value={workTypeId}
        optionLabel={(w) =>
          [w.workTypeCode, w.workTypeName].filter(Boolean).join(' \u2014 ') || w.workTypeId
        }
        onPick={setWorkTypeId}
        disabled={headerDisabled}
        emptyText="No work type on file"
      />
      <label>{t('sales.edit.areaRequested')}<input aria-label="area-requested" type="number" step="0.01" value={areaRequested} disabled={headerDisabled} onChange={(e) => setAreaRequested(e.target.value)} /></label>
      <label>{t('sales.edit.areaUnit')}<select aria-label="area-unit" value={areaUnit} disabled={headerDisabled} onChange={(e) => setAreaUnit(e.target.value)}>
        {areaUnitOptions(job.areaUnit).map((u) => <option key={u} value={u}>{unitLabel(u)}</option>)}
      </select></label>
      <label>{t('sales.edit.latitude')}<input aria-label="latitude" type="number" step="any" value={latitude} disabled={headerDisabled} onChange={(e) => setLatitude(e.target.value)} /></label>
      <label>{t('sales.edit.longitude')}<input aria-label="longitude" type="number" step="any" value={longitude} disabled={headerDisabled} onChange={(e) => setLongitude(e.target.value)} /></label>
      <label>{t('sales.edit.remarks')}<textarea aria-label="edit-remarks" value={remarks} disabled={mode === 'LOCKED'} onChange={(e) => setRemarks(e.target.value)} /></label>

      <button type="button" aria-label="save-job" onClick={() => { void save(); }} disabled={busy || mode === 'LOCKED'}>{t('sales.edit.saveChanges')}</button>
    </main>
  );
}
